import React, { FC, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./revision-history-modal.module.scss";
import clock from "../../../../../images/clock.svg";
import Modal from "../../../../../shared/modal/modal";
import draft from "../../../../../images/draft.svg";
import rejected from "../../../../../images/rejected.svg";
import approved from "../../../../../images/approve.svg";
import CustomScrollbar from "../../../../ui-kit/scrollbar/scrollbar";
import { ReportHistoryResponse } from "../../../../../services/reducers/reports-journal";

interface RevisionHistoryProps {
  onClose: () => void;
  revisionHistory: ReportHistoryResponse;
}

const RevisionHistoryModal: FC<RevisionHistoryProps> = ({
  onClose,
  revisionHistory,
}) => {
  const { t } = useTranslation();
  const [needsScroll, setNeedsScroll] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const statusIcons: Record<string, string> = {
    draft: draft,
    rejected: rejected,
    approved: approved,
    "under review": clock,
    "rejected by mohup": rejected,
    "approved by mohup": approved,
  };

  const statusIcon = statusIcons[revisionHistory.status.toLowerCase()];

  useLayoutEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const height = modal.offsetHeight;
    setNeedsScroll(height >= 650);
  }, []);

  return (
    <Modal
      ref={modalRef}
      closeClass={styles.closeBtn}
      classes={{
        modalOverlay: styles.modalOverlay,
      }}
      onClose={onClose}
      className={styles.modal}
    >
      <div
        className={`${styles.historyModal} ${needsScroll ? styles.fullHeight : ""}`}
      >
        <h2 className={styles.title}>
          {revisionHistory.cityName} {t("revision-history-modal.reportTitle")}
        </h2>
        <div className={styles.info}>
          <div className={styles.item}>
            <span className={styles.label}>
              {t("revision-history-modal.statusLabel")}:
            </span>
            <div className={styles.value}>
              <img src={statusIcon} alt={revisionHistory.status} />
              {revisionHistory.status}
            </div>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>
              {t("revision-history-modal.reportTypeLabel")}:
            </span>
            <div className={styles.value}>
              {t("revision-history-modal.creationValue")}
            </div>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>
              {t("revision-history-modal.governoratesLabel")}
            </span>
            <div className={styles.value}>{revisionHistory.governorate}</div>
          </div>
        </div>
        <div className={styles.history}>
          <span className={styles.label}>
            {t("revision-history-modal.revisionHistoryLabel")}:
          </span>
          <CustomScrollbar enabled={needsScroll} flex={1}>
            <div
              className={`${styles.revisionTimeline} ${needsScroll ? styles.scrollWrapper : ""}`}
            >
              {revisionHistory.historyLogs
                .slice()
                .sort((a, b) => b.id - a.id)
                .map((history, index, arr) => {
                  const isFirst = index === arr.length - 1;
                  const statusText = isFirst
                    ? t("revision-history-modal.createdReport")
                    : `${t("revision-history-modal.changedStatusTo")} ${history.status}`;

                  return (
                    <div key={index} className={styles.revisionEntry}>
                      <div className={styles.entryContent}>
                        <span className={styles.userName}>
                          {history.userName}
                        </span>
                        <p className={styles.status}>{statusText}</p>
                      </div>
                      <div className={styles.entryDate}>
                        {history.changedDateTime}
                      </div>
                    </div>
                  );
                })}
            </div>
          </CustomScrollbar>
        </div>
      </div>
    </Modal>
  );
};

export default RevisionHistoryModal;
